import { usePlayerContext } from 'hamrah-player';
import { round } from 'lodash';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useLoggedIn } from '@/Module/Auth/Hook';
import { logError } from '@/Module/Core/Helper';
import { useSubscriptionPlan, useVideoDuration } from '@/Module/Core/Hook';
import { CookieService } from '@/Module/Core/Service';
import { LessonBlur, LessonDetailUserLoginModal, LessonSubscriptionBlur } from '@/Module/Lesson/Component';
import { exitFullscreen, isFullscreenSupported } from '@/Module/Lesson/Helper';
import { sendLessonVideoLogsAction } from '@/Module/Lesson/Store/Action';
import { selectedLessonSectionSelector } from '@/Module/Lesson/Store/Selector/selectedLessonSectionSelector';
import { useAppDispatch, useAppSelector } from '@/Module/Store/Hook';
const LessonDetailPlayerLimitation: FC = () => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useLoggedIn();
  const {
    playerRef,
    initialed,
    setForceStopped
  } = usePlayerContext();
  const {
    isValid,
    onPlay,
    onPause,
    onTimeUpdate,
    onLoginClick
  } = useVideoDuration();
  const {
    hasAccess,
    onPlayPlan,
    onPausePlan,
    onTimeUpdatePlan
  } = useSubscriptionPlan();
  const lessonSection = useAppSelector(selectedLessonSectionSelector);
  const videoHandler = useMemo(() => {
    return {
      play: isLoggedIn ? onPlayPlan : onPlay,
      pause: isLoggedIn ? onPausePlan : onPause,
      timeUpdate: isLoggedIn ? () => onTimeUpdatePlan(playerRef.current) : () => onTimeUpdate(playerRef.current)
    };
  }, [isLoggedIn, onPlayPlan, onPlay, playerRef.current]);
  useEffect(() => {
    if (!(isLoggedIn ? !hasAccess : !isValid)) return () => {};
    if (isFullscreenSupported()) {
      exitFullscreen().catch(error => {
        logError('Failed to exit fullscreen', {
          error
        });
      });
    }
    setForceStopped(true);
    return () => {
      setForceStopped(false);
    };
  }, [hasAccess, isValid]);
  useEffect(() => {
    if (!playerRef.current || !initialed) return () => {};
    playerRef.current.on('play', videoHandler.play);
    playerRef.current.on('pause', videoHandler.pause);
    playerRef.current.on('timeupdate', videoHandler.timeUpdate);
    return () => {
      playerRef.current.off('play', videoHandler.play);
      playerRef.current.off('pause', videoHandler.pause);
      playerRef.current.off('timeupdate', videoHandler.timeUpdate);
    };
  }, [playerRef.current, initialed, videoHandler]);
  useEffect(() => {
    if (!playerRef.current) return;
    let previousPercentage = 0;
    const intervalId = setInterval(() => {
      const duration = playerRef?.current.duration();
      const currentTime = playerRef?.current.currentTime();
      const percentage = round(currentTime / duration * 100) ?? 0;
      const token = CookieService.getAccessToken();
      if (token && percentage !== previousPercentage && percentage > 0 && percentage <= 100) {
        dispatch(sendLessonVideoLogsAction({
          lessonVideoId: lessonSection?.lessonVideoId ?? 0,
          percentage: round(currentTime / duration * 100)
        }));
        previousPercentage = percentage;
      }
    }, 1000);

    // eslint-disable-next-line consistent-return
    return () => clearInterval(intervalId);
  }, [playerRef.current]);
  return <>
      {!isValid && <LessonBlur onClick={onLoginClick} />}
      {!isValid || !hasAccess && <LessonSubscriptionBlur />}
      {!isValid && <LessonDetailUserLoginModal onClick={onLoginClick} />}
    </>;
};
export default LessonDetailPlayerLimitation;